import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useTokenStore from "../../stores/tokens";
import { compatApi } from "../../utils/Intercepter";
import { useFilteredPagination } from "../../utils/PageUtil";
import { format } from "date-fns";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";

export default function PushTemplateList() {
    const { user } = useTokenStore();
    const navigation = useNavigate();

    const [templateList, setTemplateList] = useState([]);
    const [searchWord, setSearchWord] = useState("");
    const [searchOption, setSearchOption] = useState("all");
    const [filterOpen, setFilterOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const {
        currentItems,
        currentPage,
        totalPages,
        startPage,
        endPage,
        handlePageChange,
        handleNextGroup,
        handlePrevGroup,
        applySort,
        sortConfig,
        updateOriginalData,
    } = useFilteredPagination(templateList, null, 10, 5, { key: '', direction: 'asc' });

    const handleSearch = () => {
        compatApi.post('/template/search', {
            organizationCode: user?.currentOrganizationCode || '',
            searchWord,
            searchOption,
        }).then(response => {
            if (response.ok) {
                const data = response.data.data;
                handlePageChange(1);
                setTemplateList(data);
                updateOriginalData(data);
            } else {
                alert('검색에 실패했습니다.');
            }
        }).catch(() => alert('오류가 발생했습니다.'));
    };

    useEffect(() => {
        async function initialize() {
            try {
                const response = await compatApi.get('/template/find/list?organizationCode=' + user?.currentOrganizationCode);
                if (response.ok) {
                    const data = response.data.data;
                    setTemplateList(data);
                    updateOriginalData(data);
                } else {
                    alert('템플릿 목록을 가져오는 데 실패했습니다.');
                }
            } catch {
                alert('오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        }
        initialize();
    }, [user?.currentOrganizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const pageBtn = (disabled, active) => ({
        height: 30, minWidth: 30, borderRadius: 7,
        background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : disabled ? '#fafaff' : 'white',
        color: active ? 'white' : disabled ? '#c7c5d2' : '#5d5b6e',
        border: active ? 'none' : '1px solid #ececf2',
        fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 61px)', background: '#f5f4fa' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* Page header */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>푸시</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}><path d="M6 9l6 6 6-6" /></svg>
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>템플릿 목록</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
                                템플릿 목록
                            </h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>
                                자주 사용하는 푸시 알림 형식을 템플릿으로 등록하여 편리하게 관리하세요.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filter & search bar */}
                <div style={{
                    background: 'white',
                    borderRadius: 16,
                    padding: '16px 20px',
                    border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04)',
                    marginBottom: 16,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 16, flexWrap: 'wrap',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                    </div>

                    {/* Right: search */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                        <div style={{ position: 'relative' }}>
                            <button onClick={() => setFilterOpen(!filterOpen)} style={{
                                height: 38, padding: '0 30px 0 12px', borderRadius: 10,
                                background: '#fafaff', border: '1.5px solid #ececf2',
                                fontSize: 13, color: '#3a3849', fontWeight: 500,
                                cursor: 'pointer', fontFamily: 'inherit', position: 'relative',
                            }}>
                                {{ all: '전체', templateName: '템플릿 제목', msgTitle: '메시지 제목', msgBody: '메시지 내용' }[searchOption] || searchOption}
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#8b8a99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                                    <path d="M4 6l4 4 4-4" />
                                </svg>
                            </button>
                            {filterOpen && (
                                <>
                                    <div onClick={() => setFilterOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 6,
                                        background: 'white', borderRadius: 10,
                                        boxShadow: '0 8px 24px -8px rgba(60,50,120,.20)',
                                        border: '1px solid #ececf2', minWidth: 130, overflow: 'hidden',
                                    }}>
                                        {[
                                            { val: 'all', label: '전체' },
                                            { val: 'templateName', label: '템플릿 제목' },
                                            { val: 'msgTitle', label: '메시지 제목' },
                                            { val: 'msgBody', label: '메시지 내용' }
                                        ].map(({ val, label }) => (
                                            <button key={val} onClick={() => { setSearchOption(val); setFilterOpen(false); }} style={{
                                                display: 'block', width: '100%', textAlign: 'left',
                                                padding: '9px 14px',
                                                background: searchOption === val ? hexToRgba(PURPLE, .08) : 'white',
                                                border: 'none', color: searchOption === val ? PURPLE : '#3a3849',
                                                fontSize: 13, fontWeight: searchOption === val ? 600 : 500,
                                                cursor: 'pointer', fontFamily: 'inherit',
                                            }}>{label}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <input
                            type="text"
                            value={searchWord}
                            onChange={e => setSearchWord(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            placeholder="검색어를 입력 하세요"
                            style={{
                                height: 38, width: 280, borderRadius: 10, padding: '0 14px',
                                border: '1.5px solid #ececf2', background: 'white',
                                fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
                                transition: 'border-color 140ms, box-shadow 140ms',
                            }}
                            onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; }}
                            onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                        />

                        <button onClick={handleSearch} style={{
                            height: 38, padding: '0 22px', borderRadius: 10, border: 'none',
                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                            color: 'white', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                            boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                            </svg>
                            검색
                        </button>
                    </div>
                </div>

                {/* Main card */}
                <div style={{
                    background: 'white', borderRadius: 16,
                    border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden',
                }}>
                    {/* Card header */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '16px 20px',
                        background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                        borderBottom: '1px solid #ececf2',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>템플릿 목록</h2>
                            <span style={{
                                fontSize: 11, fontWeight: 600, background: 'white', color: '#5d5b6e',
                                padding: '2px 8px', borderRadius: 999, border: '1px solid #ececf2', lineHeight: 1.45,
                            }}>총 {templateList.length}건</span>
                        </div>
                        {user?.currentRole === 'MASTER' && (
                            <button onClick={() => navigation('/push/template/create')} style={{
                                height: 34, padding: '0 18px', borderRadius: 10, border: 'none',
                                background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                whiteSpace: 'nowrap', boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                            }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                템플릿 추가
                            </button>
                        )}
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                            <colgroup>
                                <col style={{ width: 60 }} /><col /><col style={{ width: 130 }} /><col style={{ width: 130 }} />
                            </colgroup>
                            <thead>
                                <tr style={{ background: '#f7f6fb' }}>
                                    {[
                                        { label: '번호', key: null, align: 'center' },
                                        { label: '템플릿 이름', key: 'templateName', align: 'left' },
                                        { label: '작성일', key: 'regDt', align: 'center' },
                                        { label: '수정일', key: 'modDt', align: 'center' },
                                    ].map((h, i) => (
                                        <th key={h.label} onClick={h.key ? () => applySort(h.key) : undefined}
                                            style={{
                                                padding: '12px 14px', fontSize: 12, fontWeight: 600, color: '#5d5b6e',
                                                textAlign: h.align,
                                                borderBottom: '1px solid #ececf2', whiteSpace: 'nowrap',
                                                cursor: h.key ? 'pointer' : 'default', userSelect: 'none',
                                                letterSpacing: '.02em',
                                            }}>
                                            {h.label}{h.key ? getSortIcon(h.key) : ''}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems?.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ padding: '48px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                                            등록된 템플릿이 없습니다.
                                        </td>
                                    </tr>
                                ) : currentItems?.map((template, index) => (
                                    <tr key={template.id || index}
                                        style={{ background: 'white', transition: 'background 100ms' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#fafaff'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9' }}>
                                            <span style={{
                                                fontSize: 11.5, color: '#8b8a99', fontWeight: 600,
                                                background: '#f4f3f9', padding: '2px 8px', borderRadius: 6,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>{(currentPage - 1) * 10 + index + 1}</span>
                                        </td>
                                        <td style={{ padding: '12px 14px', textAlign: 'left', borderBottom: '1px solid #f4f3f9', fontSize: 13 }}>
                                            <span style={{ color: MINT, fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
                                                onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                                                onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                                                onClick={() => navigation('/push/template/edit', { state: template })}>
                                                {template.templateName}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#3a3849' }}>
                                            {template.regDt ? format(new Date(template.regDt), 'yyyy-MM-dd') : '-'}
                                        </td>
                                        <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#3a3849' }}>
                                            {template.modDt ? format(new Date(template.modDt), 'yyyy-MM-dd') : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 20px', borderTop: '1px solid #ececf2',
                        background: '#fafaff',
                    }}>
                        <span style={{ fontSize: 12, color: '#8b8a99' }}>
                            {templateList.length > 0 ? (currentPage - 1) * 10 + 1 : 0} - {Math.min(currentPage * 10, templateList.length)} / {templateList.length}
                        </span>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {totalPages > 1 && (
                                <>
                                    <button onClick={handlePrevGroup} disabled={currentPage === 1} style={pageBtn(currentPage === 1, false)}>‹</button>
                                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(n => (
                                        <button key={n} onClick={() => handlePageChange(n)} style={pageBtn(false, n === currentPage)}>{n}</button>
                                    ))}
                                    <button onClick={handleNextGroup} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages, false)}>›</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
